import React, { useRef, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { HelmetProvider } from 'react-helmet-async';
import ProtectedRoute from './components/ProtectedRoute';
import PWAInstallBanner from './components/PWAInstallBanner';

import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import PublicTrackingPage from './pages/PublicTrackingPage';
import PrivacyPage from './pages/PrivacyPage';
import AboutPage from './pages/AboutPage';
import TermsPage from './pages/TermsPage';

import DashboardPage from './pages/admin/DashboardPage';
import OrdersPage from './pages/admin/OrdersPage';
import KanbanPage from './pages/admin/KanbanPage';
import OrderCreatePage from './pages/admin/OrderCreatePage';
import OrderDetailPage from './pages/admin/OrderDetailPage';
import ProductsPage from './pages/admin/ProductsPage';
import CustomersPage from './pages/admin/CustomersPage';

//tambahanridlo
import CustomerDetailPage from './pages/admin/CustomerDetailPage';
import CustomerSegmentsPage from './pages/admin/CustomerSegmentsPage';
import InventoryPage from './pages/admin/InventoryPage';
import TutorialsPage from './pages/admin/TutorialsPage';


import StaffPage from './pages/admin/StaffPage';
import CashDrawerPage from './pages/admin/CashDrawerPage';
import SettingsPage from './pages/admin/SettingsPage';
import CalendarPage from './pages/admin/CalendarPage';
import WaLogPage from './pages/admin/WaLogPage';

import ProfitLossPage from './pages/admin/reports/ProfitLossPage';
import CostsReportPage from './pages/admin/reports/CostsReportPage';
import StaffPerformancePage from './pages/admin/reports/StaffPerformancePage';
import ReceivablesPage from './pages/admin/reports/ReceivablesPage';

import StaffDashboardPage from './pages/karyawan/StaffDashboardPage';
import StaffOrdersPage from './pages/karyawan/StaffOrdersPage';
import StaffKanbanPage from './pages/karyawan/StaffKanbanPage';
import StaffOrderCreatePage from './pages/karyawan/StaffOrderCreatePage';
import StaffOrderDetailPage from './pages/karyawan/StaffOrderDetailPage';
import StaffCashDrawerPage from './pages/karyawan/StaffCashDrawerPage';
import StaffCostsPage from './pages/karyawan/StaffCostsPage';
import AdminCostsPage from './pages/admin/AdminCostsPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import StaffCalendarPage from './pages/karyawan/StaffCalendarPage';

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, staleTime: 30000 } }
});

const ADMIN_ROLES = ['owner', 'manager'];
const ALL_ROLES = ['owner', 'manager', 'staff'];
const BASE = process.env.REACT_APP_BACKEND_URL;

// ─── Google OAuth Callback Handler ───────────────────────────────────────────
function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = location.hash || '';
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) { navigate('/login'); return; }

    const sessionId = match[1];
    fetch(`${BASE}/api/auth/google-session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ session_id: sessionId }),
    })
      .then(r => r.json())
      .then(async data => {
        if (data.needs_setup) {
          sessionStorage.setItem('google_user', JSON.stringify({ email: data.email, name: data.name, picture: data.picture }));
          navigate('/register?google=true');
        } else if (data.token) {
          await refreshUser();
          const user = data.user;
          const dest = (user.role === 'owner' || user.role === 'manager') ? '/admin' : '/karyawan';
          navigate(dest, { replace: true });
        } else {
          navigate('/login');
        }
      })
      .catch(() => navigate('/login'));
  }, []); // eslint-disable-line

  return (
    <div className="flex items-center justify-center min-h-screen bg-[#f8fafc]">
      <div className="text-center space-y-3">
        <div className="w-12 h-12 border-4 border-[#0f6c44] border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="text-slate-600 text-sm font-medium">Memproses masuk dengan Google...</p>
      </div>
    </div>
  );
}

// ─── Router — detects session_id before any route runs ───────────────────────
function AppRouter() {
  const location = useLocation();
  // Synchronous check: if returning from OAuth, render AuthCallback immediately
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/track/:tracking_code" element={<PublicTrackingPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/terms" element={<TermsPage />} />

      <Route path="/admin" element={<ProtectedRoute roles={ADMIN_ROLES}><DashboardPage /></ProtectedRoute>} />
      <Route path="/admin/orders" element={<ProtectedRoute roles={ADMIN_ROLES}><OrdersPage /></ProtectedRoute>} />
      <Route path="/admin/order-board" element={<ProtectedRoute roles={ADMIN_ROLES}><KanbanPage /></ProtectedRoute>} />
      <Route path="/admin/order-create" element={<ProtectedRoute roles={ADMIN_ROLES}><OrderCreatePage /></ProtectedRoute>} />
      <Route path="/admin/orders/:id" element={<ProtectedRoute roles={ADMIN_ROLES}><OrderDetailPage /></ProtectedRoute>} />
      <Route path="/admin/products" element={<ProtectedRoute roles={ADMIN_ROLES}><ProductsPage /></ProtectedRoute>} />
      <Route path="/admin/customers" element={<ProtectedRoute roles={ADMIN_ROLES}><CustomersPage /></ProtectedRoute>} />
      
      <Route path="/admin/customers/:id" element={<ProtectedRoute roles={ADMIN_ROLES}><CustomerDetailPage /></ProtectedRoute>} />
      <Route path="/admin/segments" element={<ProtectedRoute roles={ADMIN_ROLES}><CustomerSegmentsPage /></ProtectedRoute>} />
      <Route path="/admin/inventory" element={<ProtectedRoute roles={ADMIN_ROLES}><InventoryPage /></ProtectedRoute>} />
      <Route path="/admin/tutorials" element={<ProtectedRoute roles={['owner']}><TutorialsPage /></ProtectedRoute>} />

      <Route path="/admin/staff" element={<ProtectedRoute roles={ADMIN_ROLES}><StaffPage /></ProtectedRoute>} />
      <Route path="/admin/cash-drawer" element={<ProtectedRoute roles={ADMIN_ROLES}><CashDrawerPage /></ProtectedRoute>} />
      <Route path="/admin/biaya" element={<ProtectedRoute roles={ADMIN_ROLES}><AdminCostsPage /></ProtectedRoute>} />
      <Route path="/admin/settings" element={<ProtectedRoute roles={ADMIN_ROLES}><SettingsPage /></ProtectedRoute>} />
      <Route path="/admin/calendar" element={<ProtectedRoute roles={ADMIN_ROLES}><CalendarPage /></ProtectedRoute>} />
      <Route path="/admin/wa-log" element={<ProtectedRoute roles={ADMIN_ROLES}><WaLogPage /></ProtectedRoute>} />

      <Route path="/admin/reports" element={<Navigate to="/admin/reports/profit-loss" replace />} />
      <Route path="/admin/reports/profit-loss" element={<ProtectedRoute roles={ADMIN_ROLES}><ProfitLossPage /></ProtectedRoute>} />
      <Route path="/admin/reports/costs" element={<ProtectedRoute roles={ADMIN_ROLES}><CostsReportPage /></ProtectedRoute>} />
      <Route path="/admin/reports/staff" element={<ProtectedRoute roles={ADMIN_ROLES}><StaffPerformancePage /></ProtectedRoute>} />
      <Route path="/admin/reports/receivables" element={<ProtectedRoute roles={ADMIN_ROLES}><ReceivablesPage /></ProtectedRoute>} />

      <Route path="/karyawan" element={<ProtectedRoute roles={ALL_ROLES}><StaffDashboardPage /></ProtectedRoute>} />
      <Route path="/karyawan/orders" element={<ProtectedRoute roles={ALL_ROLES}><StaffOrdersPage /></ProtectedRoute>} />
      <Route path="/karyawan/orders/:id" element={<ProtectedRoute roles={ALL_ROLES}><StaffOrderDetailPage /></ProtectedRoute>} />
      <Route path="/karyawan/order-board" element={<ProtectedRoute roles={ALL_ROLES}><StaffKanbanPage /></ProtectedRoute>} />
      <Route path="/karyawan/order-create" element={<ProtectedRoute roles={ALL_ROLES}><StaffOrderCreatePage /></ProtectedRoute>} />
      <Route path="/karyawan/cash-drawer" element={<ProtectedRoute roles={ALL_ROLES}><StaffCashDrawerPage /></ProtectedRoute>} />
      <Route path="/karyawan/biaya" element={<ProtectedRoute roles={ALL_ROLES}><StaffCostsPage /></ProtectedRoute>} />
      <Route path="/karyawan/calendar" element={<ProtectedRoute roles={ALL_ROLES}><StaffCalendarPage /></ProtectedRoute>} />

      <Route path="/" element={<LandingPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <LanguageProvider>
          <AuthProvider>
            <BrowserRouter>
              <AppRouter />
              <PWAInstallBanner />
              <Toaster richColors position="top-right" />
            </BrowserRouter>
          </AuthProvider>
        </LanguageProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

export default App;
